import * as React from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface SearchableSelectOption {
  value: string
  label: string
  disabled?: boolean
  // Internal: used for search and selection
  _searchKey?: string
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  label?: string | React.ReactNode
  error?: boolean
  errorMessage?: string
}

export const SearchableSelect = React.forwardRef<
  HTMLButtonElement,
  SearchableSelectProps
>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select option...',
      searchPlaceholder = 'Search...',
      emptyMessage = 'No results found.',
      disabled = false,
      className,
      label,
      error = false,
      errorMessage,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)

    // Create a stable map of label to value for reliable selection
    const labelToValueMap = React.useMemo(() => {
      const map = new Map<string, string>()
      options.forEach((option) => {
        // Use label as key, store the actual value
        // Filter out options with invalid labels
        if (option && option.label && typeof option.label === 'string') {
          map.set(option.label, option.value)
        }
      })
      return map
    }, [options])

    const selectedOption = options.find((option) => option && option.value === value)

    const handleSelect = (selectedValue: string) => {
      if (selectedValue === value) {
        onValueChange?.('')
      } else {
        onValueChange?.(selectedValue)
      }
      setOpen(false)
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      onValueChange?.('')
    }

    return (
      <div className="space-y-2">
        {label && (
          <label className={cn('text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70', error && 'text-destructive')}>
            {label}
          </label>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'w-full justify-between h-9',
                !selectedOption && 'text-muted-foreground',
                error && 'border-destructive',
                disabled && className?.includes('font-bold') && 'text-foreground font-bold opacity-100',
                className
              )}
            >
              <span className={cn(
                "truncate flex-1 text-left",
                className?.includes('font-bold') && 'font-bold',
                disabled && className?.includes('font-bold') && 'text-foreground font-bold opacity-100'
              )}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
              <div className="flex items-center gap-1">
                {value && !disabled && (
                  <X
                    className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                    onClick={handleClear}
                  />
                )}
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[250px] p-0" align="start">
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {options.filter(opt => opt && opt.label && opt.value).map((option) => {
                    // Use data attribute to store the actual value for reliable selection
                    return (
                      <CommandItem
                        key={`${option.value}-${option.label}`}
                        value={option.label || ''}
                        disabled={option.disabled}
                        onSelect={(selectedLabel) => {
                          // Use the labelToValueMap to get the correct value
                          // This handles case-insensitive matching and ensures correct selection
                          if (!selectedLabel || typeof selectedLabel !== 'string') {
                            return
                          }
                          
                          const actualValue = labelToValueMap.get(selectedLabel) || 
                            Array.from(labelToValueMap.entries()).find(
                              ([label]) => label && typeof label === 'string' && label.toLowerCase() === selectedLabel.toLowerCase()
                            )?.[1]
                          
                          if (actualValue) {
                            handleSelect(actualValue)
                          } else {
                            // Fallback: find by exact label match
                            const matchedOption = options.find(opt => opt && opt.label === selectedLabel)
                            if (matchedOption) {
                              handleSelect(matchedOption.value)
                            }
                          }
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value === option.value ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {error && errorMessage && (
          <p className="text-sm text-destructive mt-1">{errorMessage}</p>
        )}
      </div>
    )
  }
)

SearchableSelect.displayName = 'SearchableSelect'

